import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  useDisclosure,
  Progress,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";
import DarkHeader from "src/layout/DarkHeader";
import { BIOFLOW_SUBSCRIPTION_ROUTE, LOGIN_ROUTE } from "src/routePaths";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "src/store";
import { validateToken } from "src/validateToken";
import BioFlowPlanSelector, {
  BioFlowQuotationSelection,
} from "src/components/BioFlowPlanSelector/BioFlowPlanSelector";
import PaymentGatewayModal from "src/components/PaymentGatewayModal/PaymentGatewayModal";
import BioFlowPasswordModal from "src/components/BioFlowPasswordModal/BioFlowPasswordModal";
import { uploadFile } from "src/api";
import {
  createBioFlowOrder,
  registerBioFlowCredentials,
  repurchaseBioFlowOrder,
  verifyBioFlowCallback,
} from "src/api/bioflow";
import {
  BioFlowBillingCycle,
  BioFlowHandoffCredentials,
  BioFlowHandoffErrorCode,
  BioFlowOrderResponse,
  SUBSCRIPTION_PRODUCT_BIOFLOW,
} from "src/types/bioflow";
import { useBioFlowSubscription } from "src/hooks/bioflow/useBioFlowSubscription";
import { useBioFlowHandoff } from "src/hooks/bioflow/useBioFlowHandoff";
import { useBioFlowAccount } from "src/hooks/bioflow/useBioFlowAccount";
import { isBioFlowCredentialsRequired } from "src/utils/bioflowErrors";

const HANDOFF_ERROR_MESSAGES: Record<BioFlowHandoffErrorCode, { id: string; defaultMessage: string }> = {
  expired_token: {
    id: "bioflow.error.expiredToken",
    defaultMessage: "Your onboarding link expired. Please start again from the portal.",
  },
  invalid_token: {
    id: "bioflow.error.invalidToken",
    defaultMessage: "We could not verify your return from SALBioFlow. Please start again.",
  },
  account_not_found: {
    id: "bioflow.error.accountNotFound",
    defaultMessage: "Your SALBioFlow account is not ready yet. Open SALBioFlow from the portal and try again.",
  },
  onboarding_incomplete: {
    id: "bioflow.error.onboardingIncomplete",
    defaultMessage: "Please complete SALBioFlow onboarding before subscribing.",
  },
};

const changePaymentButtonStyle: React.CSSProperties = {
  borderRadius: "5px",
  margin: "10px auto",
  padding: "5px 10px",
  textAlign: "center",
  display: "block",
  width: "200px",
  color: "white",
  background: "#1f3f93",
};

const completeProcessButtonStyle: React.CSSProperties = {
  ...changePaymentButtonStyle,
  margin: "30px auto",
};

const BioFlowSubscription = () => {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);

  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [repurchase, setRepurchase] = useState<BioFlowOrderResponse | null>(null);
  const [changeQuotationSectionVisibility, setChangeQuotationSectionVisibility] =
    useState(false);
  const [billingCycle, setBillingCycle] = useState<BioFlowBillingCycle>("monthly");
  const [selection, setSelection] = useState<BioFlowQuotationSelection | null>(null);
  const [verifyingReturn, setVerifyingReturn] = useState(true);
  const [onboardingReady, setOnboardingReady] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const activeHandoffAttempted = useRef(false);
  const processedCallbackTokenRef = useRef<string | null>(null);
  const processedHandoffErrorRef = useRef<string | null>(null);
  const pendingCheckoutSelectionRef = useRef<BioFlowQuotationSelection | null>(null);

  const gatewayModalProps = useDisclosure();
  const uploadModalProps = useDisclosure();
  const passwordModalProps = useDisclosure();
  const [pendingSelection, setPendingSelection] = useState<BioFlowQuotationSelection | null>(null);
  const [progress, setProgress] = useState(0);
  const [isRegisteringCredentials, setIsRegisteringCredentials] = useState(false);

  const {
    quotations,
    isActive,
    loading: subscriptionLoading,
    refreshStatus,
    getQuotationForCycle,
  } = useBioFlowSubscription();
  const { startHandoff, isStarting, markOnboardingVerified, isOnboardingVerified } =
    useBioFlowHandoff();
  const {
    registered: bioflowRegistered,
    onboardingComplete,
    loading: accountLoading,
    refresh: refreshAccount,
  } = useBioFlowAccount();

  const toggleChangeQuotations = useCallback(() => {
    setChangeQuotationSectionVisibility((prev) => !prev);
  }, []);

  const refreshRepurchase = useCallback(() => {
    repurchaseBioFlowOrder()
      .then((res) => setRepurchase(res?.id ? res : null))
      .catch(() => setRepurchase(null));
  }, []);

  useLayoutEffect(() => {
    validateToken();
  }, []);

  React.useEffect(() => {
    if (success) {
      refreshRepurchase();
    } else {
      setRepurchase(null);
      setChangeQuotationSectionVisibility(false);
    }
  }, [success, refreshRepurchase]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const handoffError = params.get("error") as BioFlowHandoffErrorCode | null;
    const callback = params.get("callback");

    const cleanUrl = () => {
      history.replace(location.pathname);
    };

    if (handoffError) {
      if (processedHandoffErrorRef.current === handoffError) {
        return;
      }
      processedHandoffErrorRef.current = handoffError;
      cleanUrl();

      const message = HANDOFF_ERROR_MESSAGES[handoffError] ?? HANDOFF_ERROR_MESSAGES.invalid_token;
      toast({
        title: formatMessage({
          id: "bioflow.error.returnFailed",
          defaultMessage: "SALBioFlow onboarding failed",
        }),
        description: formatMessage(message),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
      setVerifyingReturn(false);
      return;
    }

    if (!callback) {
      return;
    }

    if (processedCallbackTokenRef.current === callback) {
      return;
    }

    processedCallbackTokenRef.current = callback;
    cleanUrl();
    setVerifyingReturn(true);

    verifyBioFlowCallback(callback)
      .then(async () => {
        markOnboardingVerified();
        const status = await refreshAccount();
        setOnboardingReady(true);
        if (status?.registered) {
          toast({
            title: formatMessage({
              id: "bioflow.onboarding.complete",
              defaultMessage: "Onboarding complete",
            }),
            description: formatMessage({
              id: "bioflow.onboarding.checkoutReady",
              defaultMessage: "Choose a plan below to activate SALBioFlow.",
            }),
            status: "success",
            duration: 6000,
            isClosable: true,
          });
        } else {
          passwordModalProps.onOpen();
        }
      })
      .catch(() => {
        toast({
          title: formatMessage({
            id: "bioflow.error.returnFailed",
            defaultMessage: "SALBioFlow onboarding failed",
          }),
          description: formatMessage(HANDOFF_ERROR_MESSAGES.invalid_token),
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      })
      .finally(() => {
        setVerifyingReturn(false);
      });
  }, [
    formatMessage,
    history,
    location.pathname,
    location.search,
    markOnboardingVerified,
    refreshAccount,
    toast,
  ]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("callback") || params.get("error")) {
      return;
    }
    if (verifyingReturn && processedCallbackTokenRef.current) {
      return;
    }
    if (accountLoading) {
      return;
    }

    setOnboardingReady(isOnboardingVerified() || onboardingComplete || isActive);
    setVerifyingReturn(false);
  }, [
    accountLoading,
    isActive,
    isOnboardingVerified,
    location.search,
    onboardingComplete,
    verifyingReturn,
  ]);

  React.useEffect(() => {
    if (isActive || onboardingComplete) {
      setOnboardingReady(true);
    }
  }, [isActive, onboardingComplete]);

  React.useEffect(() => {
    const plan = getQuotationForCycle(billingCycle);
    if (!plan) return;

    setSelection((prev) => {
      if (prev?.quotation?.id === plan.id && prev.billingCycle === billingCycle) {
        return prev;
      }
      return {
        quotation: plan,
        billingCycle,
        price: plan.price,
        selected: prev?.selected,
        proveUrl: prev?.proveUrl,
      };
    });
  }, [billingCycle, getQuotationForCycle]);

  const handleSelectMethod = (method: string) => {
    setSelection((prev) => {
      if (prev) return { ...prev, selected: method };
      const plan = getQuotationForCycle(billingCycle);
      if (!plan) return prev;
      return {
        quotation: plan,
        billingCycle,
        price: plan.price,
        selected: method,
      };
    });
  };

  const handleMedia = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selection) return;
    uploadFile(
      file,
      setProgress,
      (url) => setSelection({ ...selection, proveUrl: url }),
      () => {
        toast({
          title: formatMessage({ id: "upload.fail" }),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      },
      user?.accessToken,
    );
  };

  const subscribe = (sel: BioFlowQuotationSelection, gateway?: "tap" | "myfatoorah") => {
    if (processing || !sel.quotation?.id) return;
    setProcessing(true);

    let paymentWindow: Window | null = null;
    if (sel.selected === "card") {
      paymentWindow = window.open("", "_blank");
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.document.write("<p>Opening payment...</p>");
      }
    }

    createBioFlowOrder({
      quotationId: sel.quotation.id,
      paymentMethod:
        sel.selected === "bank" || sel.selected === "purchase"
          ? sel.selected
          : "card",
      product: SUBSCRIPTION_PRODUCT_BIOFLOW,
      gateway: gateway || "tap",
      proveUrl: sel.proveUrl,
    })
      .then((res) => {
        setProcessing(false);
        if (res?.url) {
          sessionStorage.setItem("bioflowPaymentPending", "true");
          if (paymentWindow && !paymentWindow.closed) {
            paymentWindow.location.href = res.url;
          } else {
            window.open(res.url, "_blank");
          }
          toast({
            title: formatMessage({ id: "bioflow.order.created", defaultMessage: "Order created" }),
            description: formatMessage({
              id: "bioflow.order.pending",
              defaultMessage: "Complete payment to activate SALBioFlow.",
            }),
            status: "info",
            duration: 10000,
            isClosable: true,
          });
        } else if (res?.id) {
          if (paymentWindow && !paymentWindow.closed) paymentWindow.close();
          setSuccess(true);
        }
      })
      .catch(() => {
        setProcessing(false);
        if (paymentWindow && !paymentWindow.closed) paymentWindow.close();
        toast({
          title: formatMessage({ id: "wentWrong" }),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const handleSubscribe = () => {
    if (!selection?.selected || !selection.quotation) return;

    if (!bioflowRegistered) {
      pendingCheckoutSelectionRef.current = selection;
      passwordModalProps.onOpen();
      return;
    }

    if (selection.selected === "card") {
      setPendingSelection(selection);
      gatewayModalProps.onOpen();
      return;
    }
    if (selection.selected === "bank") {
      if (!selection.proveUrl) {
        uploadModalProps.onOpen();
        return;
      }
      subscribe(selection);
      return;
    }
    if (selection.selected === "purchase") {
      subscribe(selection);
    }
  };

  const handleGatewaySelection = (gateway: "tap" | "myfatoorah") => {
    gatewayModalProps.onClose();
    if (pendingSelection) {
      subscribe(pendingSelection, gateway);
      setPendingSelection(null);
    }
  };

  const handleGatewayModalClose = () => {
    setPendingSelection(null);
    setProcessing(false);
    gatewayModalProps.onClose();
  };

  const showHandoffError = () => {
    toast({
      title: formatMessage({
        id: "bioflow.error.handoffFailed",
        defaultMessage: "Could not open SALBioFlow",
      }),
      description: formatMessage({ id: "wentWrong" }),
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  const runActiveSubscriberHandoff = useCallback(
    async (credentials?: BioFlowHandoffCredentials) => {
      setOpeningDashboard(true);
      const popup = window.open("", "_blank");
      try {
        await startHandoff(popup, credentials);
        await refreshAccount();
        passwordModalProps.onClose();
        history.replace("/portal");
      } catch (error) {
        if (isBioFlowCredentialsRequired(error)) {
          if (popup && !popup.closed) {
            popup.close();
          }
          activeHandoffAttempted.current = false;
          passwordModalProps.onOpen();
          return;
        }
        if (popup && !popup.closed) {
          popup.close();
        }
        activeHandoffAttempted.current = false;
        showHandoffError();
      } finally {
        setOpeningDashboard(false);
      }
    },
    [history, passwordModalProps, refreshAccount, startHandoff],
  );

  React.useEffect(() => {
    if (
      verifyingReturn ||
      accountLoading ||
      subscriptionLoading ||
      success ||
      openingDashboard ||
      activeHandoffAttempted.current
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get("callback")) {
      return;
    }
    if (processedCallbackTokenRef.current && verifyingReturn) {
      return;
    }

    const openDashboardForActiveSubscriber = async () => {
      const freshStatus = await refreshStatus();
      const currentlyActive = freshStatus?.active ?? isActive;
      if (!currentlyActive || activeHandoffAttempted.current) {
        return;
      }

      activeHandoffAttempted.current = true;
      await runActiveSubscriberHandoff();
    };

    void openDashboardForActiveSubscriber();
  }, [
    accountLoading,
    isActive,
    location.search,
    openingDashboard,
    refreshStatus,
    runActiveSubscriberHandoff,
    subscriptionLoading,
    success,
    verifyingReturn,
  ]);

  const handleStartOnboarding = async () => {
    try {
      await startHandoff();
      await refreshAccount();
    } catch {
      showHandoffError();
    }
  };

  const continuePendingCheckout = (sel: BioFlowQuotationSelection) => {
    if (sel.selected === "card") {
      setPendingSelection(sel);
      gatewayModalProps.onOpen();
      return;
    }
    if (sel.selected === "bank") {
      if (!sel.proveUrl) {
        setSelection(sel);
        uploadModalProps.onOpen();
        return;
      }
      subscribe(sel);
      return;
    }
    if (sel.selected === "purchase") {
      subscribe(sel);
    }
  };

  const handleCredentialsConfirm = async (credentials: BioFlowHandoffCredentials) => {
    if (isActive || activeHandoffAttempted.current) {
      await runActiveSubscriberHandoff(credentials);
      return;
    }
    setIsRegisteringCredentials(true);
    try {
      await registerBioFlowCredentials({ password: credentials.password });
      await refreshAccount();
      passwordModalProps.onClose();
      setOnboardingReady(true);
      toast({
        title: formatMessage({
          id: "bioflow.onboarding.complete",
          defaultMessage: "Onboarding complete",
        }),
        description: formatMessage({
          id: "bioflow.onboarding.checkoutReady",
          defaultMessage: "Choose a plan below to activate SALBioFlow.",
        }),
        status: "success",
        duration: 6000,
        isClosable: true,
      });
      const pending = pendingCheckoutSelectionRef.current;
      pendingCheckoutSelectionRef.current = null;
      if (pending?.selected && pending.quotation) {
        continuePendingCheckout(pending);
      }
    } catch {
      toast({
        title: formatMessage({
          id: "bioflow.error.handoffFailed",
          defaultMessage: "Could not open SALBioFlow",
        }),
        description: formatMessage({ id: "wentWrong" }),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRegisteringCredentials(false);
    }
  };

  if (!user) return <Redirect to={LOGIN_ROUTE} />;

  const repurchaseCheckoutUrl = repurchase?.url;
  const isPendingCardRepurchase =
    repurchase?.paymentMethod === "card" && !!repurchaseCheckoutUrl;

  const renderCheckoutModals = () => (
    <>
      <PaymentGatewayModal
        isOpen={gatewayModalProps.isOpen}
        onClose={handleGatewayModalClose}
        onSelect={handleGatewaySelection}
      />
      <BioFlowPasswordModal
        isOpen={passwordModalProps.isOpen}
        onClose={passwordModalProps.onClose}
        onConfirm={handleCredentialsConfirm}
        isSubmitting={isStarting || isRegisteringCredentials}
        variant="postOnboarding"
      />
      <Modal isOpen={uploadModalProps.isOpen} onClose={uploadModalProps.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody pb={6}>
            <Text color="grey" fontSize={18} fontWeight="bold">
              <FormattedMessage id="proveUrl" />
            </Text>
            <FormControl mt={4}>
              <Input type="file" onChange={handleMedia} />
            </FormControl>
            {progress > 0 && progress < 100 && <Progress value={progress} size="sm" mt={2} />}
            <Button
              mt={4}
              colorScheme="blue"
              isDisabled={!selection?.proveUrl}
              onClick={() => {
                uploadModalProps.onClose();
                if (selection) subscribe(selection);
              }}
            >
              <FormattedMessage id="subscribe" defaultMessage="Subscribe" />
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );

  if (success) {
    return (
      <>
        <DarkHeader />
        <Box padding="50px 20px">
          <Text color="green.500" textAlign="center">
            <FormattedMessage id="offerPending" />
            <br />
            <a href="https://t.me/SmartsWay125">https://t.me/SmartsWay125</a>
            {isPendingCardRepurchase ? (
              <>
                <p>
                  <a
                    style={completeProcessButtonStyle}
                    href={repurchaseCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage id="completeProcess" />
                  </a>
                </p>
                <Link
                  to={BIOFLOW_SUBSCRIPTION_ROUTE}
                  onClick={toggleChangeQuotations}
                  style={changePaymentButtonStyle}
                >
                  <FormattedMessage id="changePaymentMethod" />
                  {changeQuotationSectionVisibility ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )}
                </Link>
              </>
            ) : (
              <Link
                to={BIOFLOW_SUBSCRIPTION_ROUTE}
                onClick={toggleChangeQuotations}
                style={changePaymentButtonStyle}
              >
                <FormattedMessage id="changePaymentMethod" />
                {changeQuotationSectionVisibility ? (
                  <ChevronUpIcon />
                ) : (
                  <ChevronDownIcon />
                )}
              </Link>
            )}
          </Text>
        </Box>
        {changeQuotationSectionVisibility && (
          <Box minH="80vh" bg="gray.50" px={4} display="flex" justifyContent="center">
            <BioFlowPlanSelector
              quotations={quotations}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              selection={selection}
              onSelectMethod={handleSelectMethod}
              onSubscribe={handleSubscribe}
              processing={processing}
              isActive={isActive}
            />
          </Box>
        )}
        {renderCheckoutModals()}
      </>
    );
  }

  if (verifyingReturn || openingDashboard) {
    return (
      <>
        <DarkHeader />
        <Box minH="60vh" display="flex" alignItems="center" justifyContent="center">
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" />
            <Text color="gray.600">
              <FormattedMessage
                id={openingDashboard ? "bioflow.openingDashboard" : "bioflow.verifyingReturn"}
                defaultMessage={
                  openingDashboard
                    ? "Opening your SALBioFlow dashboard..."
                    : "Verifying your return from SALBioFlow..."
                }
              />
            </Text>
          </VStack>
        </Box>
      </>
    );
  }

  if (!onboardingReady && !isActive) {
    return (
      <>
        <DarkHeader />
        <Box minH="60vh" display="flex" alignItems="center" justifyContent="center" px={4}>
          <VStack spacing={5} maxW="480px" textAlign="center">
            <Text fontSize="xl" fontWeight="semibold">
              <FormattedMessage
                id="bioflow.onboarding.requiredTitle"
                defaultMessage="Complete SALBioFlow onboarding first"
              />
            </Text>
            <Text color="gray.600">
              <FormattedMessage
                id="bioflow.onboarding.requiredBody"
                defaultMessage="Start onboarding in SALBioFlow, then return here to choose your subscription plan."
              />
            </Text>
            <Button
              colorScheme="blue"
              size="lg"
              isLoading={isStarting}
              onClick={handleStartOnboarding}
            >
              <FormattedMessage
                id="bioflow.button.startOnboarding"
                defaultMessage="Start SALBioFlow onboarding"
              />
            </Button>
            <Link to="/portal">
              <FormattedMessage id="back" defaultMessage="Back" />
            </Link>
          </VStack>
        </Box>
        <BioFlowPasswordModal
          isOpen={passwordModalProps.isOpen}
          onClose={passwordModalProps.onClose}
          onConfirm={handleCredentialsConfirm}
          isSubmitting={isStarting || isRegisteringCredentials}
          variant="postOnboarding"
        />
      </>
    );
  }

  return (
    <>
      <DarkHeader />
      <Box minH="80vh" bg="gray.50" px={4} display="flex" justifyContent="center">
        <BioFlowPlanSelector
          quotations={quotations}
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
          selection={selection}
          onSelectMethod={handleSelectMethod}
          onSubscribe={handleSubscribe}
          processing={processing}
          isActive={isActive}
        />
      </Box>

      {renderCheckoutModals()}
    </>
  );
};

export default BioFlowSubscription;
